// @ts-check
// CLIENT-SAFE image/descriptor degrade guard (task #67 hardening, #93 tier).
//
// WHY A SEPARATE, ZOD-FREE MODULE
// -------------------------------
// lib/normalizeOffers.js owns the SERVER boundary guard (validateAndDegradeImages)
// but it imports lib/resolveImage (the full asset manifest) AND zod, so it must
// NEVER be pulled into a client bundle. The Overmind CLIENT operators still ingest
// offer arrays from /api/* into the store, and while those endpoints already
// normalize+validate server-side, a stale/proxied/edge response could still drift
// a descriptor into the exact "[object Object]" src shape we hotfixed in prod.
//
// This module is the PURE-JS, zod-free, manifest-free half of that guard so a
// client operator can degrade a malformed descriptor at the store boundary
// WITHOUT importing zod or the manifest into first-load JS. normalizeOffers reuses
// the same primitive so the drop policy lives in ONE place.
//
// POLICY (identical to normalizeOffers' server guard): NEVER throw; a ResolvedImage
// MUST be null or an object with a string `.src`. Anything else on a scalar image
// field is nulled; anything else in a gallery array is filtered out. Only the
// image/descriptor fields already known to regress are touched — the rest of the
// offer is left byte-for-byte intact, so valid data is a no-op (zero behavior
// change).

/** @typedef {import('./schemas').ResolvedImage} ResolvedImage */
/** @typedef {import('./schemas').Offer} Offer */

// The image fields degraded here. Kept in lockstep with normalizeOffers'
// IMAGE_SCALAR_FIELDS / IMAGE_ARRAY_FIELDS and schemas.ShopItemImagesSchema.
const IMAGE_SCALAR_FIELDS = ['imageUrl', 'imageOverride', 'fullSetImage']
const IMAGE_ARRAY_FIELDS = ['galleryImages', 'fullSetGalleryImages']

/**
 * A value is a VALID (resolved) image descriptor when it is null/undefined (field
 * simply absent) or an object carrying a string `.src`. A bare string, a number,
 * or an object without a string src is the "[object Object]"/broken-src class.
 *
 * @param {unknown} v
 * @returns {boolean} true when `v` is a bad scalar descriptor (present but invalid)
 */
function isBadScalarDescriptor(v) {
  return (
    v != null &&
    !(typeof v === 'object' && typeof (/** @type {any} */ (v).src) === 'string')
  )
}

/**
 * Degrade the image/descriptor fields of a SINGLE offer IN PLACE, dropping any
 * that are not valid ResolvedImage descriptors. Never throws; returns the same
 * (possibly mutated) item. `label` is only used for the dev-only warn.
 *
 * @param {any} item
 * @param {string} [label]
 * @returns {any} the same item, with invalid image fields nulled/filtered
 */
export function degradeOfferImages(item, label) {
  if (!item || typeof item !== 'object') return item

  let degraded = false

  for (const f of IMAGE_SCALAR_FIELDS) {
    if (isBadScalarDescriptor(item[f])) {
      item[f] = null
      degraded = true
    }
  }
  for (const f of IMAGE_ARRAY_FIELDS) {
    if (Array.isArray(item[f])) {
      const before = item[f].length
      item[f] = item[f].filter(
        (v) => v && typeof v === 'object' && typeof v.src === 'string'
      )
      if (item[f].length !== before) degraded = true
    }
  }
  if (item.category && isBadScalarDescriptor(item.category.pictogram)) {
    item.category = { ...item.category, pictogram: null }
    degraded = true
  }

  // Dev-only signal so a boundary drift is loud in development but silent (and
  // cheap) in production. Guarded so the string work is dead-code-eliminated in
  // prod builds and never spams a user's console.
  if (degraded && process.env.NODE_ENV !== 'production') {
    const id = (item && (item.id || item.itemType || item.name)) || label || '<unknown>'
    // eslint-disable-next-line no-console
    console.warn(
      `[imageGuards] offer "${id}" carried an invalid image descriptor at a client ` +
        'store boundary; degraded the offending field(s) so no "[object Object]" src reaches a component.'
    )
  }

  return item
}

/**
 * Degrade the image fields of an ARRAY of offers in place. Non-arrays pass
 * through untouched (so an operator can guard a possibly-missing payload safely).
 *
 * @param {any} items
 * @returns {any} the same array (or the untouched non-array value)
 */
export function degradeOffersImages(items) {
  if (!Array.isArray(items)) return items
  for (const item of items) degradeOfferImages(item)
  return items
}

/**
 * Degrade a bare ARRAY of ResolvedImage descriptors in place, filtering out any
 * that are not valid ({ src: string }). Used for nested descriptor arrays that do
 * NOT live on the offer image fields — e.g. a turnkey option's `images` gallery.
 * Returns a NEW filtered array (so the caller reassigns) when input is an array;
 * non-arrays pass through untouched.
 *
 * @param {any} arr
 * @returns {any} a filtered array (or the untouched non-array value)
 */
export function degradeDescriptorArray(arr) {
  if (!Array.isArray(arr)) return arr
  return arr.filter((v) => v && typeof v === 'object' && typeof v.src === 'string')
}
